/**
 * AppLayout — Tüm oturum açmış sayfalar için ortak sayfa kabuğu.
 * Sayfa başlığı, kullanıcı bilgisi, çıkış butonu ve içerik yuvası.
 */
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

interface AppLayoutProps {
  title: string;
  subtitle?: string;
  /** Sağ üst köşede ekstra içerik (örn. "+ Yeni Proje" butonu). */
  actions?: ReactNode;
  /** Container genişliği: 'narrow' | 'default' | 'wide' (varsayılan: default). */
  width?: 'narrow' | 'default' | 'wide';
  children: ReactNode;
}

function getInitials(name: string | undefined, username: string): string {
  const source = (name && name.trim()) || username || '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  const first = parts[0] ?? '';
  const second = parts[1] ?? '';
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
}

export function AppLayout({
  title,
  subtitle,
  actions,
  width = 'default',
  children,
}: AppLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const displayName = user?.display_name || user?.username || 'Kullanıcı';

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const containerClass =
    width === 'narrow'
      ? 'container container--narrow'
      : width === 'wide'
        ? 'container container--wide'
        : 'container';

  return (
    <div className="app-layout">
      <header className="app-layout__header" role="banner">
        <div className={`${containerClass} app-layout__bar`}>
          <div className="app-layout__brand">
            <span className="app-layout__logo" aria-hidden="true">🎓</span>
            <span className="app-layout__brand-text">Mulakid</span>
          </div>
          <div className="app-layout__user">
            <span className="user-chip">
              <span aria-hidden="true">{displayName}</span>
              <span className="user-chip__avatar" aria-hidden="true">
                {getInitials(user?.display_name, user?.username ?? '')}
              </span>
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleLogout}
            >
              Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className={containerClass}>
        <div className="page-header">
          <div>
            <h1 className="page-title">{title}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="page-header__actions">{actions}</div>}
        </div>
        {children}
      </main>

      <style>{`
        .app-layout { display: flex; flex-direction: column; min-height: 100vh; }
        .app-layout__header {
          position: sticky;
          top: 0;
          z-index: 50;
          background: var(--surface);
          border-bottom: 1px solid var(--border);
          box-shadow: var(--shadow-sm);
        }
        .app-layout__bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--sp-4);
          padding-top: 12px;
          padding-bottom: 12px;
        }
        .app-layout__brand {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 800;
          color: var(--text-main);
        }
        .app-layout__logo {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: var(--primary-soft);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }
        .app-layout__brand-text {
          font-size: var(--font-size-lg);
          letter-spacing: -0.01em;
        }
        .app-layout__user {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .page-header__actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        @media (max-width: 640px) {
          .app-layout__bar { flex-wrap: wrap; }
          .app-layout__user { gap: 6px; }
          .user-chip { padding: 4px 4px 4px 10px; }
        }
      `}</style>
    </div>
  );
}
